import { inject, Injector, Provider, Service } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, isObservable, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';

import { translate, TranslationToken } from '@ideals/components/translate';
import { PageTitle } from '@ideals/models';

const TITLE_KEY = 'title';
const TITLE_SUFFIX: TranslationToken = { raw: 'Ideals' };

@Service({ autoProvided: false })
class PageTitleStrategy extends TitleStrategy {
  readonly #injector = inject(Injector);
  readonly #title = inject(Title);

  readonly #title$ = new Subject<Observable<PageTitle>[]>();

  constructor() {
    super();

    // Resolved on the first navigation, not injected: this strategy is built together with the
    // `Router`, and constructing `TranslateService` that early fires its fallback-language request
    // through interceptors that inject back into services still under construction (NG0200).
    const language$ = this.#title$.pipe(
      take(1),
      switchMap(() => this.#injector.get(TranslateService).onLangChange),
      startWith(null),
    );
    const titles$ = this.#title$.pipe(switchMap((_titles$) => combineLatest(_titles$)));

    combineLatest([titles$, language$]).subscribe(([pageTitles]) => {
      const translateService = this.#injector.get(TranslateService);
      const title = pageTitles
        .filter((item, index) => !pageTitles[index + 1]?.replaceParent)
        .map((pageTitle) => translate(translateService, pageTitle.value))
        .join(' | ');

      this.#title.setTitle(title);
    });
  }

  updateTitle(routerState: RouterStateSnapshot): void {
    const pageTitles: Observable<PageTitle>[] = [];
    let route = routerState.root;

    while (route) {
      const pageTitle = route.data[TITLE_KEY] as PageTitle | Observable<PageTitle>;

      if (pageTitle) {
        pageTitles.push(isObservable(pageTitle) ? pageTitle : of(pageTitle));
      }

      route = route.firstChild!;
    }

    pageTitles.push(of({ value: TITLE_SUFFIX }));

    this.#title$.next(pageTitles);
  }
}

export function providePageTitleStrategy(): Provider[] {
  return [{
    provide: TitleStrategy,
    useClass: PageTitleStrategy,
  }];
}
